import { useBetSliderStore, useConfigurationStore } from '@stores'
import { ViewportType } from 'game-ui-types'
import gsap from 'gsap'
import React, { useEffect, useRef, useState } from 'react'

interface UseLongPressAnimationProps {
    ref: React.RefObject<HTMLElement | null>
    pressDelay?: number
    scale?: number
    animationDuration?: number
    keyCode?: string
    isSpaceKeyDisabled?: boolean
    isEnabled: boolean // indicates if the long press animation is enabled
    onPressStart?: () => void
    onPressComplete?: (isFromMouse: boolean) => void
    onRelease?: () => void
}

const useLongPressAnimation = ({
    ref,
    pressDelay = 1,
    scale = 1.25,
    animationDuration = 0.8,
    keyCode,
    isSpaceKeyDisabled,
    isEnabled,
    onPressStart,
    onPressComplete,
    onRelease,
}: UseLongPressAnimationProps) => {
    const animationRef = useRef<gsap.core.Tween | null>(null)
    const [isCompleted, setIsCompleted] = useState(false)
    const isCompletedRef = useRef(isCompleted)
    const [isTriggeredByKey, setIsTriggeredByKey] = useState(false)
    const viewport = useConfigurationStore((state) => state.viewport)
    const isTriggeredByKeyRef = useRef(isTriggeredByKey)
    const disabledRef = useRef(false)
    const isScrolling = useBetSliderStore((state) => state.isScrolling)
    const isScrollingRef = useRef(isScrolling)
    const isSpaceKeyUpTriggered = useRef(true)

    useEffect(() => {
        isCompletedRef.current = isCompleted
    }, [isCompleted])

    useEffect(() => {
        isScrollingRef.current = isScrolling
    }, [isScrolling])

    useEffect(() => {
        isTriggeredByKeyRef.current = isTriggeredByKey
    }, [isTriggeredByKey])

    useEffect(() => {
        if (!isEnabled) return

        const button = ref.current

        if (!button) return

        const handleKeyUpNotEnabled = (event: KeyboardEvent) => {
            if (event.code === keyCode) isSpaceKeyUpTriggered.current = true
        }

        const finishAnimation = () => {
            gsap.killTweensOf(button)

            animationRef.current = gsap.to(button, {
                scale: 1,
                duration: 0.15,
                onComplete: () => {
                    if (isEnabled && onRelease) {
                        onRelease()
                    }
                },
            })
        }

        if (!isEnabled) {
            if (keyCode) {
                document.addEventListener('keyup', handleKeyUpNotEnabled)
            }

            if (animationRef.current) finishAnimation()

            return
        }

        const startAnimation = (isFromMouse: boolean) => {
            animationRef.current = gsap.to(button, {
                scale,
                duration: animationDuration,
                delay: pressDelay,
                onComplete: () => {
                    if (onPressComplete) {
                        onPressComplete(isFromMouse)

                        gsap.to(button, {
                            scale: 1,
                            duration: 0.15,
                        })
                    }
                },
                onStart: () => {
                    if (onPressStart) {
                        onPressStart()
                    }
                },
            })
        }

        const handleMouseDown = (e: MouseEvent) => {
            if (e?.button === 2) return

            if (isScrollingRef.current) return

            if (!isCompletedRef.current) {
                startAnimation(true)
                setIsCompleted(true)
            }
        }

        const handleMouseUp = (e: MouseEvent) => {
            if (e?.button === 2) return

            if (disabledRef.current) return

            if (isCompletedRef.current && !isTriggeredByKeyRef.current) {
                finishAnimation()
                setIsCompleted(false)
            }
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            // For fullscreen changes while holding the space key
            if (!isSpaceKeyUpTriggered.current) {
                return
            }

            isSpaceKeyUpTriggered.current = false

            if (isSpaceKeyDisabled || disabledRef.current || isScrollingRef.current) return

            if (event.code === keyCode && !isCompletedRef.current) {
                startAnimation(false)
                setIsCompleted(true)
                setIsTriggeredByKey(true)
            }
        }

        const handleKeyUp = (event: KeyboardEvent) => {
            if (isSpaceKeyDisabled || disabledRef.current) return

            if (event.code === keyCode && isCompletedRef.current && isTriggeredByKeyRef.current) {
                finishAnimation()
                setIsCompleted(false)
                setIsTriggeredByKey(false)
            }

            isSpaceKeyUpTriggered.current = true
        }

        const handleBlur = () => {
            isSpaceKeyUpTriggered.current = true

            if (
                keyCode &&
                isCompletedRef.current &&
                isTriggeredByKeyRef.current &&
                !disabledRef.current &&
                !isSpaceKeyDisabled
            ) {
                finishAnimation()
                setIsCompleted(false)
                setIsTriggeredByKey(false)
            }
        }

        button.addEventListener('pointerdown', handleMouseDown)
        button.addEventListener('pointerup', handleMouseUp)

        if (viewport !== ViewportType.DESKTOP) {
            button.addEventListener('pointerleave', handleMouseUp)
        }

        document.addEventListener('pointerup', handleMouseUp)

        window.addEventListener('blur', handleBlur)

        if (keyCode) {
            document.addEventListener('keydown', handleKeyDown)
            document.addEventListener('keyup', handleKeyUp)
        }

        return () => {
            button.removeEventListener('pointerup', handleMouseUp)
            button.removeEventListener('pointerdown', handleMouseDown)

            if (viewport !== ViewportType.DESKTOP) {
                button.removeEventListener('pointerleave', handleMouseUp)
            }

            document.removeEventListener('pointerup', handleMouseUp)
            window.removeEventListener('blur', handleBlur)

            if (keyCode) {
                document.removeEventListener('keydown', handleKeyDown)
                document.removeEventListener('keyup', handleKeyUp)
                document.removeEventListener('keyup', handleKeyUpNotEnabled)

                // Clean up if event listeners removed
                setIsCompleted(false)
                setIsTriggeredByKey(false)
            }
        }
    }, [isEnabled, viewport, isSpaceKeyDisabled])

    const kill = () => {
        if (animationRef.current) {
            animationRef.current.kill()
            gsap.set(ref.current, { scale: 1 })
            animationRef.current = null
        }
    }

    // we need to handle internally the disabled state
    // because there are cases where turbo spin is forced stopped
    // and we need to prevent mouse up event from being triggered
    const disable = () => {
        disabledRef.current = true
    }

    const enable = () => {
        disabledRef.current = false
        setIsCompleted(false)
        setIsTriggeredByKey(false)
    }

    return {
        disable,
        enable,
        kill,
    }
}

export default useLongPressAnimation
